import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2, ChevronRight, Sparkles, MessageSquare, Truck, Users } from 'lucide-react';
import emailjs from '@emailjs/browser';

function Contact({ isMobile }) {
  const ref = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const controls = useAnimation();
  
  const [formState, setFormState] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    subject: 'General Inquiry'
  });
  
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    errorMessage: ''
  });

  const [activeInput, setActiveInput] = useState(null);

  const subjects = [
    'General Inquiry',
    'Bulk Order Request',
    'Quality Concern',
    'Partnership Opportunity',
    'Delivery Schedule',
    'Product Availability'
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: "Location",
      details: "Zvido Business Centre, Chitungwiza",
      subtext: "Open for walk-ins and pre-orders",
      gradient: "from-emerald-500 to-teal-400",
      bgGradient: "from-emerald-900/20 to-teal-900/10",
      link: "https://maps.google.com",
      delay: 0
    },
    {
      icon: Phone,
      title: "Phone",
      details: "+263 783 90 7172",
      subtext: "Mon-Sun: 6:00 AM - 8:00 PM",
      gradient: "from-blue-500 to-cyan-400",
      bgGradient: "from-blue-900/20 to-cyan-900/10",
      link: "tel:+263783907172",
      delay: 0.1
    },
    {
      icon: Mail,
      title: "Email",
      details: "sirhenrybutchery@gmail.com",
      subtext: "Response within 2 hours",
      gradient: "from-amber-500 to-orange-400",
      bgGradient: "from-amber-900/20 to-orange-900/10",
      link: "mailto:sirhenrybutchery@gmail.com",
      delay: 0.2
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "6:00 AM - 8:00 PM",
      subtext: "7 Days a Week",
      gradient: "from-purple-500 to-pink-400",
      bgGradient: "from-purple-900/20 to-pink-900/10",
      delay: 0.3
    }
  ];

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    }
  };

  const floatAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: false, errorMessage: '' });

    try {
      // Simulate API call with EmailJS integration
      // Replace with your actual EmailJS service ID, template ID, and public key
      await emailjs.sendForm(
        'YOUR_SERVICE_ID',
        'YOUR_TEMPLATE_ID',
        formRef.current,
        'YOUR_PUBLIC_KEY'
      );
      
      setStatus({ submitting: false, submitted: true, error: false, errorMessage: '' });
      setFormState({ name: '', phone: '', email: '', message: '', subject: 'General Inquiry' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus(prev => ({ ...prev, submitted: false }));
      }, 5000);
    } catch (error) {
      setStatus({ 
        submitting: false, 
        submitted: false, 
        error: true, 
        errorMessage: 'Failed to send message. Please try again or call us directly.' 
      });
    }
  };

  return (
    <section 
      id="contact" 
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 30% 20%, #0a0a0a 0%, #000000 100%)'
      }}
      ref={ref}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <motion.div 
          animate={floatAnimation}
          className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-emerald-500/5 to-teal-500/5 blur-3xl"
        ></motion.div>
        <motion.div 
          animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 1 } }}
          className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-gradient-to-r from-amber-500/5 to-orange-500/5 blur-3xl"
        ></motion.div>
        
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite'
          }}></div>
        </div>
      </div>

      {/* Floating Icons */}
      <motion.div 
        animate={floatAnimation}
        className="absolute top-32 left-10 text-5xl opacity-10 hidden lg:block"
      >
        ✉️
      </motion.div>
      <motion.div 
        animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 0.5 } }}
        className="absolute bottom-32 right-10 text-5xl opacity-10 hidden lg:block"
      >
        📞
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <motion.div
              animate={pulseAnimation}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur-md"></div>
              <div className="relative bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                24/7 SUPPORT
              </div>
            </motion.div>
          </div>

          <h2 className={`font-bold mb-6 ${isMobile ? 'text-4xl' : 'text-6xl'}`}>
            <span className="bg-gradient-to-r from-emerald-200 via-white to-teal-200 bg-clip-text text-transparent">
              Contact Us
            </span>
            <span className="block bg-gradient-to-r from-amber-200 via-white to-amber-200 bg-clip-text text-transparent">
              Today!
            </span>
          </h2>
          
          <motion.p 
            variants={itemVariants}
            className="text-gray-400 text-xl max-w-3xl mx-auto mb-16 leading-relaxed"
          >
            Have questions about our premium cuts? Ready to place an order? Our team is standing by to provide 
            expert guidance and ensure you get exactly what you need.
          </motion.p>
        </motion.div>

        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-12`}>
          {/* Left Side - Contact Info */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className={`${isMobile ? 'w-full' : 'w-1/2'}`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <motion.a
                    key={index}
                    href={info.link}
                    target={info.link ? "_blank" : undefined}
                    rel={info.link ? "noopener noreferrer" : undefined}
                    variants={itemVariants}
                    whileHover="hover"
                    custom={info.delay}
                    className="relative group block"
                  >
                    {/* Glow Effect */}
                    <div className={`absolute -inset-1 bg-gradient-to-r ${info.gradient} rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
                    
                    {/* Card */}
                    <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 p-6 transition-all duration-300 group-hover:border-gray-700">
                      <div className={`absolute inset-0 opacity-5 ${info.bgGradient}`}></div>
                      
                      <div className="relative flex flex-col items-center text-center">
                        <motion.div
                          animate={floatAnimation}
                          className={`mb-4 w-14 h-14 rounded-2xl bg-gradient-to-r ${info.gradient} flex items-center justify-center shadow-xl`}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </motion.div>
                        
                        <h3 className="text-lg font-bold text-white mb-2">{info.title}</h3>
                        <p className="text-xl font-semibold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent mb-2">
                          {info.details}
                        </p>
                        <p className="text-gray-400 text-sm">{info.subtext}</p>
                        
                        {info.link && (
                          <motion.div
                            initial={{ x: -5 }}
                            animate={{ x: 0 }}
                            transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
                            className="mt-4 text-xs text-gray-500 flex items-center gap-1"
                          >
                            Click to {info.title === 'Email' ? 'email' : info.title === 'Phone' ? 'call' : 'open map'}
                            <ChevronRight className="w-3 h-3" />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            {/* Stats Banner */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-8 border border-gray-800 mb-8"
            hidden>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Why Customers Choose Us
              </h3>
              <div className="space-y-4">
                {[
                  { icon: '🚚', text: 'Free Delivery on orders above $100', color: 'text-emerald-400' },
                  { icon: '⏱️', text: 'Average response time: 15 minutes', color: 'text-blue-400' },
                  { icon: '🎁', text: 'Special discounts for bulk orders', color: 'text-purple-400' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xl">{item.icon}</span>
                    <span className={`text-sm ${item.color}`}>{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CTA Banner */}
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 to-red-600/20"></div>
              <div className="relative bg-gradient-to-r from-amber-900/30 to-red-900/30 p-6 border border-amber-500/30 rounded-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-red-500 flex items-center justify-center">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">Need Immediate Assistance?</h4>
                    <p className="text-amber-200 text-sm">Call us now for urgent orders</p>
                  </div>
                  <motion.a
                    href="tel:+263783907172"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="ml-auto bg-gradient-to-r from-amber-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-amber-500/25"
                  >
                    Call Now
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className={`${isMobile ? 'w-full' : 'w-1/2'}`}
          >
            <div className="relative">
              {/* Form Glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 via-amber-500/10 to-red-500/10 rounded-3xl blur-xl"></div>
              
              {/* Form Container */}
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 p-8 shadow-2xl">
                {/* Success/Error Messages */}
                <AnimatePresence>
                  {status.submitted && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-emerald-400" />
                        <div>
                          <h4 className="text-white font-bold">Message Sent Successfully!</h4>
                          <p className="text-emerald-300 text-sm">We'll get back to you within 2 hours.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  {status.error && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-4 rounded-xl bg-gradient-to-r from-red-900/30 to-rose-900/30 border border-red-500/30"
                    >
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-6 h-6 text-red-400" />
                        <div>
                          <h4 className="text-white font-bold">Error Sending Message</h4>
                          <p className="text-red-300 text-sm">{status.errorMessage}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-emerald-400" />
                  Send Us a Message
                </h3>
                <p className="text-gray-400 mb-8">Fill out the form below and we'll respond promptly</p>

                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  {/* Subject Selector */}
                  <div hidden>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Subject</label>
                    <div className="flex flex-wrap gap-2">
                      {subjects.map((subject) => (
                        <motion.button
                          key={subject}
                          type="button"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setFormState(prev => ({ ...prev, subject }))}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            formState.subject === subject
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                          }`}
                        >
                          {subject}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name Input */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Your Name *
                      </label>
                      <div className={`relative transition-all duration-300 ${
                        activeInput === 'name' ? 'scale-[1.02]' : ''
                      }`}>
                        <input 
                          type="text" 
                          name="name"
                          value={formState.name}
                          onChange={handleInputChange}
                          onFocus={() => setActiveInput('name')}
                          onBlur={() => setActiveInput(null)}
                          required 
                          className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                          placeholder="John Doe"
                        />
                        {activeInput === 'name' && (
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -right-2 -top-2 w-4 h-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                          ></motion.div>
                        )}
                      </div>
                    </div>

                    {/* Phone Input */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <div className={`relative transition-all duration-300 ${
                        activeInput === 'phone' ? 'scale-[1.02]' : ''
                      }`}>
                        <input 
                          type="tel" 
                          name="phone"
                          value={formState.phone}
                          onChange={handleInputChange}
                          onFocus={() => setActiveInput('phone')}
                          onBlur={() => setActiveInput(null)}
                          required 
                          className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                          placeholder="+263 78 390 7172"
                        />
                        {activeInput === 'phone' && (
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -right-2 -top-2 w-4 h-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                          ></motion.div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Email Input */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <div className={`relative transition-all duration-300 ${
                      activeInput === 'email' ? 'scale-[1.02]' : ''
                    }`}>
                      <input 
                        type="email" 
                        name="email"
                        value={formState.email}
                        onChange={handleInputChange}
                        onFocus={() => setActiveInput('email')}
                        onBlur={() => setActiveInput(null)}
                        className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all"
                        placeholder="john@example.com"
                      />
                      {activeInput === 'email' && (
                        <motion.div 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -right-2 -top-2 w-4 h-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                        ></motion.div>
                      )}
                    </div>
                  </div>

                  {/* Message Textarea */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Message *
                    </label>
                    <div className={`relative transition-all duration-300 ${
                      activeInput === 'message' ? 'scale-[1.02]' : ''
                    }`}>
                      <textarea 
                        name="message" 
                        value={formState.message}
                        onChange={handleInputChange}
                        onFocus={() => setActiveInput('message')}
                        onBlur={() => setActiveInput(null)}
                        rows="4" 
                        required 
                        className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="Tell us about your requirements, bulk order needs, or any special requests..."
                      ></textarea>
                      {activeInput === 'message' && (
                        <motion.div 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -right-2 -top-2 w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        ></motion.div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={status.submitting}
                    whileHover={{ scale: status.submitting ? 1 : 1.05 }}
                    whileTap={{ scale: status.submitting ? 1 : 0.95 }}
                    className={`w-full py-4 rounded-xl font-bold text-lg shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 ${
                      status.submitting
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-emerald-500/40'
                    }`}
                  >
                    {status.submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                        <Sparkles className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>

                  {/* Privacy Note */}
                  <p className="text-gray-500 text-xs text-center">
                    By submitting this form, you agree to our privacy policy. We'll never share your information.
                  </p>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        @keyframes gridMove {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(60px) translateX(60px); }
        }
        
        .form-input-glow:focus {
          box-shadow: 0 0 20px rgba(var(--color-rgb), 0.3);
        }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus {
          -webkit-text-fill-color: white;
          -webkit-box-shadow: 0 0 0px 1000px rgba(30, 41, 59, 0.5) inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </section>
  );
}

export default Contact;